import { FC, ReactNode, useEffect, useRef, useState } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import Stack, { StackProps } from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";

import { getFadedColor } from "./ChartColors";
import { ChartLegendDot } from "./ChartLegendDot";

import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

const LABEL_WRAP_WIDTH = 200;

export type ChartLegendItemProps = Omit<BoxProps, "direction" | "alignItems" | "gap" | "component" | "children"> & {
  variant?: "default" | "faded";
  value: ReactNode;
  color: string;
  label: ReactNode;
  unit?: "absolute" | "percentage";
  wrap?: boolean;
  tooltip?: ReactNode;
};

export const ChartLegendItem = ({
  variant = "default",
  value,
  color,
  label,
  unit = "absolute",
  wrap = false,
  tooltip,
  ...rest
}: ChartLegendItemProps) => {
  const theme = useTheme();
  const labelRef = useRef<HTMLDivElement>(null);
  const [shouldLabelWrap, setShouldLabelWrap] = useState<boolean | undefined>(undefined);

  const isButton = !!rest.onClick;

  useEffect(() => {
    if (wrap && labelRef.current) {
      // Only wrap text if the auto width is greater than 200px
      const textWidth = labelRef.current.scrollWidth;
      setShouldLabelWrap(textWidth >= LABEL_WRAP_WIDTH);
    } else {
      setShouldLabelWrap(undefined);
    }
  }, [wrap]);

  return (
    <Box
      {...rest}
      display="flex"
      width="100%"
      component={isButton ? "button" : "div"}
      sx={{
        flexDirection: "row",
        alignItems: "center",
        gap: 3,
        backgroundColor: "transparent",
        border: "none",
        borderRadius: 0.5,
        padding: 0,
        textAlign: "start",
        ...(isButton && {
          cursor: "pointer",
          transition: "outline 100ms ease-out",
          outline: "0px solid transparent",
          outlineOffset: 2,
          ["&:focus-visible"]: {
            outline: `3px solid ${theme.palette.border.brand.moderate}`,
          },
        }),
      }}
    >
      <Stack flex="1 1 auto" minWidth={0} direction="row" alignItems="center" gap={1.5}>
        <ChartLegendDot flexShrink={0} backgroundColor={variant === "faded" ? getFadedColor(theme) : color} />
        <Stack direction="row" flex="1 1 auto" minWidth={0}>
          <DashboardTypography
            ref={labelRef}
            variant="body2"
            vibe="text-neutral-quiet"
            sx={{
              ...(wrap
                ? shouldLabelWrap
                  ? { width: LABEL_WRAP_WIDTH }
                  : { whiteSpace: "nowrap" }
                : { minWidth: 0, whiteSpace: "nowrap", textOverflow: "ellipsis", overflowX: "hidden" }),
            }}
          >
            {label}
          </DashboardTypography>
          {tooltip && (
            <DashboardInformationTooltip
              title={tooltip}
              placement="top"
              alignOnText
              alignOffset="2px"
              sx={{ marginInlineStart: 0.5 }}
            />
          )}
        </Stack>
      </Stack>
      <DashboardTypography variant="subtitle2" sx={{ flexShrink: 0 }} vibe="text-neutral-intense">
        {value === "" ? "—" : value}
        {unit === "percentage" && "%"}
      </DashboardTypography>
    </Box>
  );
};

type ChartLegendItemSkeletonProps = Omit<StackProps, "direction" | "alignItems" | "gap" | "children">;

export const ChartLegendItemSkeleton: FC<ChartLegendItemSkeletonProps> = (props) => {
  return (
    <Stack width="100%" direction="row" alignItems="center" gap={3} {...props}>
      <Stack flex={1} direction="row" alignItems="center" gap={1.5}>
        <DashboardSkeleton variant="circular" width={16} height={16} sx={{ flexShrink: 0 }} />
        <DashboardSkeleton variant="text" fontSize="body2" sx={{ flexGrow: 1 }} />
      </Stack>
      <DashboardSkeleton variant="text" width={20} fontSize="subtitle2" />
    </Stack>
  );
};
